/**
 * CompetencyRubricAdmin Component
 * Admin interface for managing per-company competency rubrics
 * Allows rating competencies by role with Ideal/Acceptable/custom tags
 */

import React, { useState } from "react";
import {
  useGetCompanyRubricQuery,
  useUpdateRubricCellMutation,
  useAddRubricRoleMutation,
  useRemoveRubricRoleMutation,
  useAddRubricCustomTagMutation,
  useRemoveRubricCustomTagMutation,
  usePublishRubricMutation,
  useUnpublishRubricMutation,
  useUpdateRubricRoleMutation,
} from "../../../services/api";
import "./CompetencyRubricAdmin.css";

// Default rating options
const DEFAULT_RATINGS = [
  { name: "Ideal", color: "#11734B" },
  { name: "Acceptable", color: "#D4EDBC" },
];

/**
 * CompetencyRubricAdmin
 * @param {string} companyId - The company ID to load rubric for
 */
export default function CompetencyRubricAdmin({ companyId }) {
  // State for UI interactions
  const [activeCell, setActiveCell] = useState(null); // { valueIndex, competencyIndex, roleIndex }
  const [showAddRole, setShowAddRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [showAddTag, setShowAddTag] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState("#6B7280");
  const [editingRole, setEditingRole] = useState(null);
  const [editRoleName, setEditRoleName] = useState("");

  // Fetch rubric data
  const {
    data: rubric,
    isLoading,
    isError,
    error,
  } = useGetCompanyRubricQuery(companyId, {
    skip: !companyId,
  });

  // Mutations
  const [updateCell, { isLoading: isUpdatingCell }] = useUpdateRubricCellMutation();
  const [addRole, { isLoading: isAddingRole }] = useAddRubricRoleMutation();
  const [updateRole, { isLoading: isUpdatingRole }] = useUpdateRubricRoleMutation();
  const [removeRole, { isLoading: isRemovingRole }] = useRemoveRubricRoleMutation();
  const [addCustomTag, { isLoading: isAddingTag }] = useAddRubricCustomTagMutation();
  const [removeCustomTag, { isLoading: isRemovingTag }] = useRemoveRubricCustomTagMutation();
  const [publishRubric, { isLoading: isPublishing }] = usePublishRubricMutation();
  const [unpublishRubric, { isLoading: isUnpublishing }] = useUnpublishRubricMutation();

  // Combined rating options (default + custom)
  const allRatings = [...DEFAULT_RATINGS, ...(rubric?.customTags || [])];

  // Handle cell click - open rating dropdown
  const handleCellClick = (valueIndex, competencyIndex, roleIndex) => {
    if (activeCell?.valueIndex === valueIndex &&
        activeCell?.competencyIndex === competencyIndex &&
        activeCell?.roleIndex === roleIndex) {
      setActiveCell(null);
    } else {
      setActiveCell({ valueIndex, competencyIndex, roleIndex });
    }
  };

  // Handle rating selection
  const handleRatingSelect = async (rating) => {
    if (!activeCell || !rubric?._id) return;

    try {
      await updateCell({
        rubricId: rubric._id,
        valueIndex: activeCell.valueIndex,
        competencyIndex: activeCell.competencyIndex,
        roleIndex: activeCell.roleIndex,
        rating: rating || null, // null to clear
      }).unwrap();
      setActiveCell(null);
    } catch (err) {
      console.error("Failed to update cell:", err);
    }
  };

  // Handle add role
  const handleAddRole = async () => {
    if (!newRoleName.trim() || !rubric?._id) return;

    try {
      await addRole({
        rubricId: rubric._id,
        name: newRoleName.trim(),
      }).unwrap();
      setNewRoleName("");
      setShowAddRole(false);
    } catch (err) {
      console.error("Failed to add role:", err);
    }
  };

  // Handle update role name
  const handleUpdateRole = async (roleIndex) => {
    if (!editRoleName.trim() || !rubric?._id) return;

    try {
      await updateRole({
        rubricId: rubric._id,
        roleIndex,
        name: editRoleName.trim(),
      }).unwrap();
      setEditingRole(null);
      setEditRoleName("");
    } catch (err) {
      console.error("Failed to update role:", err);
    }
  };

  // Handle remove role
  const handleRemoveRole = async (roleIndex) => {
    if (!rubric?._id) return;
    if (!window.confirm("Are you sure? This will remove all ratings for this role.")) return;

    try {
      await removeRole({
        rubricId: rubric._id,
        roleIndex,
      }).unwrap();
    } catch (err) {
      console.error("Failed to remove role:", err);
    }
  };

  // Handle add custom tag
  const handleAddTag = async () => {
    if (!newTagName.trim() || !rubric?._id) return;

    try {
      await addCustomTag({
        rubricId: rubric._id,
        name: newTagName.trim(),
        color: newTagColor,
      }).unwrap();
      setNewTagName("");
      setNewTagColor("#6B7280");
      setShowAddTag(false);
    } catch (err) {
      console.error("Failed to add custom tag:", err);
    }
  };

  // Handle remove custom tag
  const handleRemoveTag = async (tagIndex) => {
    if (!rubric?._id) return;
    if (!window.confirm("Remove this tag? Cells using it will be cleared.")) return;

    try {
      await removeCustomTag({
        rubricId: rubric._id,
        tagIndex,
      }).unwrap();
    } catch (err) {
      console.error("Failed to remove tag:", err);
    }
  };

  // Handle publish/unpublish
  const handleTogglePublish = async () => {
    if (!rubric?._id) return;

    try {
      if (rubric.isPublished) {
        await unpublishRubric(rubric._id).unwrap();
      } else {
        await publishRubric(rubric._id).unwrap();
      }
    } catch (err) {
      console.error("Failed to toggle publish:", err);
    }
  };

  // Get rating for a specific cell
  const getCellRating = (valueIndex, competencyIndex, roleIndex) => {
    const row = rubric?.tableData?.find(
      (r) => r.valueIndex === valueIndex && r.competencyIndex === competencyIndex
    );
    if (!row) return null;
    const ratingData = row.ratings?.find((r) => r.roleIndex === roleIndex);
    return ratingData?.rating || null;
  };

  // Get pill class based on rating
  const getPillClass = (rating) => {
    if (!rating) return "";
    if (rating === "Ideal") return "cra-pill-ideal";
    if (rating === "Acceptable") return "cra-pill-acceptable";
    return "cra-pill-custom";
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="cra-section">
        <div className="cra-header">
          <h3 className="cra-title">Competency Rubric</h3>
        </div>
        <div className="cra-loading">Loading rubric...</div>
      </div>
    );
  }

  // Error state - check if it's "no competency set" error
  if (isError) {
    const errorMessage = error?.data?.message || "";
    const isNoCompetencySet = errorMessage.toLowerCase().includes("competency set");

    return (
      <div className="cra-section">
        <div className="cra-header">
          <h3 className="cra-title">Competency Rubric</h3>
        </div>
        {isNoCompetencySet ? (
          <div className="cra-empty">
            <p>No competency set is assigned to this company.</p>
            <p>Assign a competency set above to enable the rubric.</p>
          </div>
        ) : (
          <div className="cra-error">
            Failed to load rubric: {errorMessage || "Unknown error"}
          </div>
        )}
      </div>
    );
  }

  // Group table data by value for rowSpan calculation
  const tableRows = rubric?.tableData || [];
  const roles = rubric?.roles || [];

  return (
    <div className="cra-section">
      {/* Header with publish button */}
      <div className="cra-header">
        <div className="cra-header-left">
          <h3 className="cra-title">Competency Rubric</h3>
          {rubric?.isPublished && (
            <span className="cra-published-badge">Published</span>
          )}
        </div>
        <div className="cra-header-actions">
          <button
            className="cra-add-tag-btn"
            onClick={() => setShowAddTag(true)}
          >
            + Add Tag
          </button>
          <button
            className="cra-add-role-btn"
            onClick={() => setShowAddRole(true)}
          >
            + Add Role
          </button>
          <button
            className={`cra-publish-btn ${rubric?.isPublished ? "cra-unpublish" : ""}`}
            onClick={handleTogglePublish}
            disabled={isPublishing || isUnpublishing}
          >
            {rubric?.isPublished ? "Unpublish" : "Publish"} →
          </button>
        </div>
      </div>

      {/* Custom Tags Manager */}
      {rubric?.customTags?.length > 0 && (
        <div className="cra-tags-bar">
          <span className="cra-tags-label">Custom Tags:</span>
          {rubric.customTags.map((tag, index) => (
            <span
              key={index}
              className="cra-tag-chip"
              style={{ backgroundColor: tag.color }}
            >
              {tag.name}
              <button
                className="cra-tag-remove"
                onClick={() => handleRemoveTag(index)}
                disabled={isRemovingTag}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Rubric Table */}
      <div className="cra-table-wrap">
        <table className="cra-table">
          <thead>
            <tr>
              <th className="cra-col-values">Values</th>
              <th className="cra-col-competency">Competency</th>
              {roles.map((role, index) => (
                <th key={index} className="cra-col-role">
                  {editingRole === index ? (
                    <div className="cra-role-edit">
                      <input
                        type="text"
                        value={editRoleName}
                        onChange={(e) => setEditRoleName(e.target.value)}
                        className="cra-role-input"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleUpdateRole(index);
                          if (e.key === "Escape") {
                            setEditingRole(null);
                            setEditRoleName("");
                          }
                        }}
                      />
                      <button
                        className="cra-role-save"
                        onClick={() => handleUpdateRole(index)}
                        disabled={isUpdatingRole}
                      >
                        ✓
                      </button>
                    </div>
                  ) : (
                    <div className="cra-role-header">
                      <span
                        className="cra-role-name"
                        onClick={() => {
                          setEditingRole(index);
                          setEditRoleName(role.name);
                        }}
                        title="Click to edit"
                      >
                        {role.name}
                      </span>
                      {roles.length > 1 && (
                        <button
                          className="cra-role-remove"
                          onClick={() => handleRemoveRole(index)}
                          disabled={isRemovingRole}
                          title="Remove role"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {tableRows.map((row, rowIndex) => (
              <tr
                key={`${row.valueIndex}-${row.competencyIndex}`}
                className={row.valueIndex % 2 === 1 ? "cra-group-alt" : ""}
              >
                {/* Values column with rowSpan */}
                {row.isFirstInValue && (
                  <td
                    className="cra-values"
                    rowSpan={row.valueRowSpan}
                  >
                    {row.valueName}
                  </td>
                )}

                {/* Competency column */}
                <td className="cra-competency">{row.competencyName}</td>

                {/* Rating cells */}
                {row.ratings.map((ratingData, rIndex) => {
                  const isActive =
                    activeCell?.valueIndex === row.valueIndex &&
                    activeCell?.competencyIndex === row.competencyIndex &&
                    activeCell?.roleIndex === ratingData.roleIndex;
                  const rating = ratingData.rating;

                  return (
                    <td
                      key={rIndex}
                      className={`cra-rating-cell ${isActive ? "cra-cell-active" : ""}`}
                      onClick={() =>
                        handleCellClick(
                          row.valueIndex,
                          row.competencyIndex,
                          ratingData.roleIndex
                        )
                      }
                    >
                      {rating ? (
                        <span className={`cra-pill ${getPillClass(rating)}`}>
                          {rating}
                        </span>
                      ) : (
                        <span className="cra-cell-empty">—</span>
                      )}

                      {/* Rating dropdown */}
                      {isActive && (
                        <div
                          className={`cra-dropdown ${rowIndex >= tableRows.length - 2 ? "cra-dropdown-above" : ""}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {allRatings.map((r, i) => (
                            <button
                              key={i}
                              className="cra-dropdown-item"
                              onClick={() => handleRatingSelect(r.name)}
                              disabled={isUpdatingCell}
                            >
                              <span
                                className="cra-dropdown-color"
                                style={{ backgroundColor: r.color }}
                              />
                              {r.name}
                            </button>
                          ))}
                          <button
                            className="cra-dropdown-item cra-dropdown-clear"
                            onClick={() => handleRatingSelect(null)}
                            disabled={isUpdatingCell}
                          >
                            Clear
                          </button>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Role Modal */}
      {showAddRole && (
        <div className="cra-modal-overlay" onClick={() => setShowAddRole(false)}>
          <div className="cra-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cra-modal-header">
              <h4>Add New Role Column</h4>
              <button
                className="cra-modal-close"
                onClick={() => setShowAddRole(false)}
              >
                ×
              </button>
            </div>
            <div className="cra-modal-body">
              <label className="cra-modal-label">Role Name</label>
              <input
                type="text"
                className="cra-modal-input"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="e.g., Team Lead"
                autoFocus
              />
            </div>
            <div className="cra-modal-footer">
              <button
                className="cra-modal-cancel"
                onClick={() => setShowAddRole(false)}
              >
                Cancel
              </button>
              <button
                className="cra-modal-confirm"
                onClick={handleAddRole}
                disabled={!newRoleName.trim() || isAddingRole}
              >
                {isAddingRole ? "Adding..." : "Add Role"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Tag Modal */}
      {showAddTag && (
        <div className="cra-modal-overlay" onClick={() => setShowAddTag(false)}>
          <div className="cra-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cra-modal-header">
              <h4>Add Custom Rating Tag</h4>
              <button
                className="cra-modal-close"
                onClick={() => setShowAddTag(false)}
              >
                ×
              </button>
            </div>
            <div className="cra-modal-body">
              <label className="cra-modal-label">Tag Name</label>
              <input
                type="text"
                className="cra-modal-input"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="e.g., Developing"
                autoFocus
              />
              <label className="cra-modal-label">Color</label>
              <div className="cra-color-picker">
                {["#6B7280", "#EF4444", "#F59E0B", "#8B5CF6", "#EC4899", "#06B6D4"].map(
                  (color) => (
                    <button
                      key={color}
                      className={`cra-color-option ${newTagColor === color ? "cra-color-selected" : ""}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewTagColor(color)}
                    />
                  )
                )}
              </div>
            </div>
            <div className="cra-modal-footer">
              <button
                className="cra-modal-cancel"
                onClick={() => setShowAddTag(false)}
              >
                Cancel
              </button>
              <button
                className="cra-modal-confirm"
                onClick={handleAddTag}
                disabled={!newTagName.trim() || isAddingTag}
              >
                {isAddingTag ? "Adding..." : "Add Tag"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close dropdown */}
      {activeCell && (
        <div
          className="cra-backdrop"
          onClick={() => setActiveCell(null)}
        />
      )}
    </div>
  );
}
